import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { DeviceService } from '../../../../../device/device.service';
import { DeviceDto, LoggerDto } from '../../../../../../../api-main';
import { getEmptyBody } from '../../getRequestBodys';
import { ValueDataModel } from '../../../../models/ValueData.model';
import { DisabledDirective } from '../../../../../../shared/directives/disabled.directive';
import { SelectingValueDataService } from '../selecting-value-data.service';
import { EmuListInputAutocompleteComponent } from '../../../../../../shared/components/inputs/emu-list-input-autocomplete/emu-list-input-autocomplete.component';

@Component({
  selector: 'app-settings-device',
  templateUrl: './value-display-settings-device.component.html',
  styleUrls: ['./value-display-settings-device.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DisabledDirective, EmuListInputAutocompleteComponent],
})
export class ValueDisplaySettingsDeviceComponent implements OnInit, OnDestroy {
  private deviceService = inject(DeviceService);
  private selectingValueDataService = inject(SelectingValueDataService);

  @Input() data: ValueDataModel;

  @Input() reloadDeviceValues$: Subject<number>;

  @Input() reloadDevice$: Subject<number>;

  deviceInput$ = new Subject<string>();

  loading$ = new BehaviorSubject(false);

  devices: DeviceDto[] = [];

  devices$: Observable<DeviceDto[]>;

  //set when only 1 device
  readonly = false;

  private _selectedDevice: DeviceDto;

  private unsubscribe = new Subject<void>();

  get selectedDevice(): DeviceDto {
    if (this.data.deviceId != this._selectedDevice?.device_id) return null;
    return this._selectedDevice;
  }

  set selectedDevice(value: DeviceDto) {
    this._selectedDevice = value;
    if (value?.device_id != null) {
      this.assignDeviceId(value.device_id);
      this.reloadDeviceValues$.next(value.device_id);
    }
  }

  ngOnInit() {
    this.loadDevices();
  }

  assignDeviceId(id: number) {
    if (this.data.deviceId == id) return;
    this.data.deviceId = id;
    this.data.valueId = null;
  }

  resetValues() {
    this.data.deviceId = null;
    this.data.valueId = null;
  }

  private loadDevices() {
    let loggerId: number;
    const device$ = this.reloadDevice$.pipe(
      tap(() => {
        this.selectedDevice = null;
        this.loading$.next(true);
      }),
      takeUntil(this.unsubscribe),
      switchMap((id) => {
        loggerId = id;

        if (this.selectingValueDataService.savedDevice[loggerId])
          return of(this.selectingValueDataService.savedDevice[loggerId]);

        return this.deviceService.getDeviceList(getEmptyBody(), id).pipe(
          map((o) =>
            o.data.sort((a, b) =>
              a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: 'base',
              }),
            ),
          ),
        );
      }),
      tap((data) => {
        this.selectingValueDataService.savedDevice[loggerId] = data;
      }),
    );
    device$.pipe(takeUntil(this.unsubscribe)).subscribe((devices) => {
      this.loading$.next(false);
      this.devices = devices;
      if (devices.length == 1) {
        this.selectedDevice = devices[0];
        this.readonly = true;
      } else {
        this.readonly = false;
      }
      this.setupDevicesFilter(devices);

      if (this.data.deviceId != null)
        this.selectedDevice = devices.find((o) => o.device_id == this.data.deviceId);
    });
  }

  setupDevicesFilter(devices: DeviceDto[]) {
    this.devices$ = this.deviceInput$.pipe(
      startWith(''),
      distinctUntilChanged(),
      map((term: DeviceDto | string) => {
        if (!term || typeof term != 'string' || term.length == 0) return devices;

        return devices.filter((device: DeviceDto) => {
          const formatValue = (value: string) => value.toUpperCase().trim().replaceAll(' ', '');

          return formatValue(device.name).includes(formatValue(term));
        });
      }),
    );
  }

  displayDeviceFn(item: LoggerDto): string {
    return item ? `${item.name}` : '';
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
